import React, { FC, ReactNode } from 'react';

import { useField } from 'formik';

import {
  Box,
  FormControlLabel,
  SwitchProps,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material';

import { IOSSwitch } from '~components/form/base-switch/base-switch.styles';
import { AppTypography } from '~components/ui/typography/app-typography';

type BaseSwitchProps = {
  name: string;
  label?: string | ReactNode;
  caption?: ReactNode;
  sxLabel?: SxProps<Theme>;
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
} & Omit<SwitchProps, 'checked' | 'name'>;

export const BaseSwitch: FC<BaseSwitchProps> = ({
  name,
  label,
  caption,
  sxLabel,
  labelPlacement = 'end',
  onChange,
  onBlur,
  ...props
}) => {
  const theme = useTheme();
  const [, meta, helpers] = useField<boolean>(name);

  // Без подписи - просто сам свитч
  if (!label) {
    return (
      <IOSSwitch
        checked={Boolean(meta.value)}
        onChange={(e, value) => {
          helpers.setValue(value);
          onChange?.(e, value);
        }}
        onBlur={(e) => {
          helpers.setTouched(true);
          onBlur?.(e);
        }}
        {...props}
      />
    );
  }

  const isSimpleLabel = typeof label === 'string';
  const preparedLabel = isSimpleLabel ? (
    <Box display={'flex'} flexDirection={'column'} gap={0.5} mx={1}>
      <AppTypography
        size={{ xs: 'body-md' }}
        weight={'semibold'}
        color={'fog.25'}
        sx={{ lineHeight: '1rem' }}
      >
        {label}
      </AppTypography>
      {caption && (
        <AppTypography
          size="body-caption"
          color={'fog.25'}
          sx={{ lineHeight: '12px' }}
        >
          {caption}
        </AppTypography>
      )}
    </Box>
  ) : (
    <Box
      display={'flex'}
      flexDirection={'column'}
      sx={{
        ...(labelPlacement === 'end' || labelPlacement === 'start'
          ? {
              margin: theme.spacing(
                0,
                labelPlacement === 'start' ? 1 : 0,
                0,
                labelPlacement === 'end' ? 1 : 0,
              ),
            }
          : {}),
      }}
    >
      <Box
        sx={{
          ...theme.typography['body-sm'],
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        {label}
      </Box>

      {caption && (
        <AppTypography size="body-caption" sx={{ mt: 0.5 }}>
          {caption}
        </AppTypography>
      )}
    </Box>
  );

  return (
    <Box display={'flex'} flexDirection={'column'}>
      <FormControlLabel
        labelPlacement={labelPlacement}
        control={
          <IOSSwitch
            checked={Boolean(meta.value)}
            onChange={(e, value) => {
              helpers.setValue(value);
              onChange?.(e, value);
            }}
            onBlur={(e) => {
              helpers.setTouched(true);
              onBlur?.(e);
            }}
            {...props}
          />
        }
        label={preparedLabel}
        sx={{
          marginLeft: 0,
          marginRight: 0,
          ...sxLabel,
        }}
      />
    </Box>
  );
};
